import { useEffect, useMemo, useState } from "react"
import firebase from "firebase"
import { X, MessageSquare, ArrowRight, BookmarkPlus, Trash2, Check } from "lucide-react"

import { db, auth } from "../../../../utils/firebase"
import Textarea from "../../../../components/ui/inputs/textarea"
import Input from "../../../../components/ui/inputs/input"
import Select from "../../../../components/ui/select/select"

import "./send-message-modal.scss"

// The greeting is stamped onto the front of the persisted message, so when the
// modal re-opens an existing message we peel a leading "Hi X," / "Dear X," /
// "Hello X," line back off to recover the editable body. Conservative: only at
// the very start, and only when the comma is present.
const stripGreeting = (text) => (text || "").replace(/^(hi|hello|dear)\s+[^,\n]{1,60},\s*/i, "")

/**
 * First step of the send flow, shown when the Send action on a quote card is
 * clicked. The agent writes an optional personal note that appears at the top of
 * the client's online quote (a "note from your agent" block above the ticket).
 *
 * The note opens with a locked "Hi {first name}," greeting nested inside the
 * input - it reads as part of the message but can't be edited - and the agent's
 * own words follow it. Messages can be saved to the agent's account
 * (users/{uid}/quote_messages) and pre-filled from a dropdown on later sends.
 *
 * On continue it persists the full message (greeting + body) onto the quote doc
 * (so the next re-send pre-fills and the publish picks it up) and hands the text
 * up so the parent can carry it through the rest of the send flow.
 */
export default function SendMessageModal({ quote, enquiryID, onClose, onContinue }) {
	const [body, setBody] = useState(() => stripGreeting(quote?.agent_message || ""))
	const [saving, setSaving] = useState(false)

	// The enquiry's client first name drives the locked greeting.
	const [clientFirstName, setClientFirstName] = useState("")
	const greeting = useMemo(() => `Hi ${clientFirstName || "there"},`, [clientFirstName])

	// The agent's saved messages (users/{uid}/quote_messages), newest first.
	const [savedMessages, setSavedMessages] = useState([])
	const [selectedSavedId, setSelectedSavedId] = useState("")
	// Save-to-account state: the name for the new saved message, in-flight flag
	// and a brief "Saved" confirmation.
	const [saveName, setSaveName] = useState("")
	const [savingMessage, setSavingMessage] = useState(false)
	const [savedFlash, setSavedFlash] = useState(false)

	// Deliberately NO Escape / outside-click close: the quote modals only close
	// via the X or their action buttons, so a half-written message can't be lost.

	// Resolve the client's first name for the greeting (best-effort).
	useEffect(() => {
		let cancelled = false
		if (!enquiryID) return undefined
		const loadClient = async () => {
			try {
				const enquiry = (await db.doc(`enquiries/${enquiryID}`).get()).data() || {}
				if (!enquiry.client) return
				const client = (await db.doc(`clients/${enquiry.client}`).get()).data() || {}
				if (!cancelled && client.first_name) setClientFirstName(String(client.first_name).trim())
			} catch (err) {
				// Non-fatal - the greeting just falls back to "Hi there,".
			}
		}
		loadClient()
		return () => {
			cancelled = true
		}
	}, [enquiryID])

	// Load the agent's saved messages.
	const loadSavedMessages = async () => {
		if (!auth.currentUser?.uid) return
		try {
			const docs = await db.collection(`users/${auth.currentUser.uid}/quote_messages`).orderBy("created", "desc").get()
			setSavedMessages(docs.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
		} catch (err) {
			console.error("Could not load saved messages:", err)
		}
	}
	useEffect(() => {
		loadSavedMessages()
		// Once on open - the list only changes through the save/delete actions
		// below, which reload it themselves.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Dropdown options: saved-message id -> its name.
	const savedOptions = useMemo(() => Object.fromEntries(savedMessages.map((message) => [message.id, message.title || "Saved message"])), [savedMessages])

	const insertSavedMessage = (id) => {
		const saved = savedMessages.find((message) => message.id === id)
		if (!saved) return
		setSelectedSavedId(id)
		setBody(stripGreeting(saved.text || ""))
	}

	// Save the current body to the agent's account under the typed name.
	const handleSaveMessage = async () => {
		const text = (body || "").trim()
		const title = (saveName || "").trim()
		if (!text || !title || savingMessage || !auth.currentUser?.uid) return
		setSavingMessage(true)
		try {
			await db.collection(`users/${auth.currentUser.uid}/quote_messages`).add({
				title,
				text,
				created: firebase.firestore.FieldValue.serverTimestamp(),
			})
			setSaveName("")
			setSavedFlash(true)
			setTimeout(() => setSavedFlash(false), 2000)
			await loadSavedMessages()
		} catch (err) {
			console.error("Could not save the message to your account:", err)
		} finally {
			setSavingMessage(false)
		}
	}

	// Remove the currently-selected saved message from the account.
	const handleDeleteSaved = async () => {
		if (!selectedSavedId || !auth.currentUser?.uid) return
		try {
			await db.doc(`users/${auth.currentUser.uid}/quote_messages/${selectedSavedId}`).delete()
			setSelectedSavedId("")
			await loadSavedMessages()
		} catch (err) {
			console.error("Could not delete the saved message:", err)
		}
	}

	const handleContinue = async () => {
		if (saving) return
		const trimmedBody = (body || "").trim()
		// The persisted message leads with the locked greeting; an empty body
		// means no note at all (we never send a greeting-only message).
		const message = trimmedBody ? `${greeting}\n${trimmedBody}` : ""
		setSaving(true)
		try {
			// Persist onto the quote doc so it survives for the next re-send and the
			// publish (in the email modal) projects it onto the public page.
			if (enquiryID && quote?.id) {
				await db.doc(`enquiries/${enquiryID}/quotes/${quote.id}`).set({ agent_message: message }, { merge: true })
			}
			onContinue(message)
		} catch (err) {
			console.error("Could not save the quote message:", err)
			// Non-fatal: still continue so a transient write error doesn't block the
			// send. The message threads through in-memory regardless.
			onContinue(message)
		}
	}

	return (
		<div className="send-message-overlay">
			<div className="send-message-modal" role="dialog" aria-modal="true" aria-label="Add a message for the client">
				<button type="button" className="send-message-close" onClick={() => !saving && onClose()} aria-label="Close">
					<X size={18} />
				</button>

				<div className="send-message-head">
					<span className="send-message-icon">
						<MessageSquare size={20} />
					</span>
					<div className="send-message-head-text">
						<h3>Add a message for the client</h3>
						<p>This shows at the top of their online quote.</p>
					</div>
				</div>

				{savedMessages.length > 0 && (
					<div className="send-message-saved-row">
						<Select
							className="send-message-saved-select"
							options={savedOptions}
							selected={selectedSavedId}
							placeholder="Pre-fill from a saved message"
							onSelect={({ option }) => insertSavedMessage(option)}
						/>
						{selectedSavedId && (
							<button type="button" className="send-message-saved-delete" onClick={handleDeleteSaved} aria-label="Delete this saved message" title="Delete this saved message">
								<Trash2 size={15} />
							</button>
						)}
					</div>
				)}

				{/* The greeting is locked - it renders inside the same input surface so
				    the note reads as one message, but only the body is editable. */}
				<div className="send-message-field">
					<div className="send-message-composer">
						<span className="send-message-greeting">{greeting}</span>
						<Textarea
							placeholder="e.g. great speaking today! I've put this together for your group's September trip - any tweaks, just let me know."
							value={body}
							onChange={setBody}
							rows={9}
						/>
					</div>
				</div>

				{/* Save the typed message back to the agent's account for next time. */}
				<div className="send-message-save-row">
					<Input
						wrapperClass="send-message-save-name"
						type="text"
						placeholder="Name this message to save it"
						value={saveName}
						onChange={setSaveName}
					/>
					<button
						type="button"
						className={["send-message-save-btn", savedFlash ? "is-saved" : ""].join(" ").trim()}
						onClick={handleSaveMessage}
						disabled={savingMessage || !(body || "").trim() || !(saveName || "").trim()}>
						{savedFlash ? <Check size={15} /> : <BookmarkPlus size={15} />}
						{savedFlash ? "Saved" : savingMessage ? "Saving..." : "Save message"}
					</button>
				</div>

				<div className="send-message-actions">
					<button type="button" className="send-message-continue" onClick={handleContinue} disabled={saving}>
						{saving ? "Saving..." : "Continue"}
						{!saving && <ArrowRight size={16} />}
					</button>
				</div>
			</div>
		</div>
	)
}
